import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsTouchUi } from '@/shared/lib/media';

import { Button } from './Button';
import { Icons } from './icons';
import styles from './MultiFilter.module.css';
import { Popover } from './Popover';

export interface MultiFilterOption {
  value: string;
  label: string;
  /** A color dot before the name (resource labels). */
  color?: string | null;
}

interface Props {
  /** The category's name ("Status", "Labels"): the trigger's text and the popover's name. */
  label: string;
  options: readonly MultiFilterOption[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  /** Lists longer than this get a search field (default 7). */
  searchFrom?: number;
  searchPlaceholder?: string;
  emptyText?: string;
  /** Marks (and keeps visible) selected values that are no longer offered, e.g. a deleted label. */
  unavailableLabel?: string;
  /** How such a value is named (default: the value itself, e.g. an ID is not readable). */
  unavailableName?: (value: string) => string;
  testId?: string;
}

/**
 * One filter category as a multi-select (OR inside the category): a trigger that shows how many
 * values are chosen, and a popover with an explicit "All", a checkbox per value (color dot and
 * a name that truncates, the full name in the tooltip), an optional search and "Clear". Ticking a
 * value keeps the popover open, so several can be chosen in one go. The same interaction as the
 * board filters; it works with the keyboard (Tab / Space) and by touch.
 */
export function MultiFilter({
  label,
  options,
  selected: committed,
  onChange: commit,
  searchFrom = 7,
  searchPlaceholder,
  emptyText,
  unavailableLabel,
  unavailableName,
  testId,
}: Props) {
  const { t } = useTranslation();
  const groupId = useId();
  const touch = useIsTouchUi();
  const [query, setQuery] = useState('');
  // The parent owns the selection and may apply it late (the address changes in a transition), but a
  // checkbox has to answer the click at once: the choice is shown from here until the parent's
  // selection changes, and further clicks build on it rather than on a stale list.
  const [pending, setPending] = useState<string[] | null>(null);
  const committedKey = committed.join('\u0000');
  const [seenKey, setSeenKey] = useState(committedKey);
  if (seenKey !== committedKey) {
    setSeenKey(committedKey);
    setPending(null);
  }
  const selected: readonly string[] = pending ?? committed;
  const onChange = (next: string[]) => {
    setPending(next);
    commit(next);
  };
  const count = selected.length;
  const known = new Set(options.map((option) => option.value));
  // a chosen value that is gone (a deleted label) stays listed so that it can be unticked
  const missing = selected.filter((value) => !known.has(value));
  const needle = query.trim().toLocaleLowerCase();
  const shown = options.filter(
    (option) => needle === '' || option.label.toLocaleLowerCase().includes(needle),
  );
  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };
  return (
    <Popover
      label={label}
      width={280}
      trigger={(props, open) => (
        <button
          type="button"
          {...props}
          className={`${styles.trigger} ${count > 0 ? styles.on : ''}`}
          data-open={open ? 'true' : undefined}
          data-testid={testId}
        >
          <span className={styles.triggerText}>
            {count > 0 ? t('filter.categoryCount', { name: label, count }) : label}
          </span>
          <Icons.chevronDown />
        </button>
      )}
    >
      <div className={styles.popover}>
        {options.length > searchFrom && (
          <input
            type="search"
            className={styles.search}
            value={query}
            autoFocus={!touch}
            placeholder={searchPlaceholder ?? t('common.search')}
            aria-label={searchPlaceholder ?? t('common.search')}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        )}
        <fieldset className={styles.group} aria-labelledby={`${groupId}-legend`}>
          <legend id={`${groupId}-legend`} className="visually-hidden">
            {label}
          </legend>
          {needle === '' && (
            <label className={styles.option}>
              <input
                type="checkbox"
                checked={count === 0}
                onChange={() => {
                  if (count > 0) onChange([]);
                }}
              />
              <span className={styles.name}>{t('filter.optionAll')}</span>
            </label>
          )}
          {shown.map((option) => (
            <label key={option.value} className={styles.option} title={option.label}>
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => {
                  toggle(option.value);
                }}
              />
              {option.color && (
                <span aria-hidden className={styles.dot} style={{ background: option.color }} />
              )}
              <span className={styles.name}>{option.label}</span>
            </label>
          ))}
          {needle === '' &&
            missing.map((value) => (
              <label key={value} className={styles.option} data-missing>
                <input
                  type="checkbox"
                  checked
                  onChange={() => {
                    toggle(value);
                  }}
                />
                <span className={styles.name}>
                  {unavailableName ? unavailableName(value) : value}
                  {' — '}
                  {unavailableLabel ?? t('filter.unavailable')}
                </span>
              </label>
            ))}
          {shown.length === 0 && (
            <p className={styles.empty}>{emptyText ?? t('common.noResults')}</p>
          )}
        </fieldset>
        <div className={styles.footer}>
          <span className={styles.count}>{t('filter.labelsSelected', { count })}</span>
          <Button
            size="sm"
            variant="ghost"
            disabled={count === 0}
            onClick={() => {
              onChange([]);
            }}
          >
            {t('filter.clear')}
          </Button>
        </div>
      </div>
    </Popover>
  );
}
